import {
  ColorLensOutlined,
  DescriptionOutlined,
  FolderOpenOutlined,
  HeadphonesOutlined,
  ImageOutlined,
  PictureAsPdfOutlined,
  RequestQuoteOutlined,
  InsertDriveFileOutlined,
  TextSnippetOutlined,
  HomeOutlined,
  VideocamOutlined,
  ArticleOutlined,
  PeopleOutline,
} from '@mui/icons-material';
import { cloneElement } from 'react';

export const fileTypeChecks = {
  isFileFolder: (file) => file?.format === 'Folder' && !file?.entity,
  isFileImage: (file) =>
    file?.content_type?.includes('image/png') ||
    file?.content_type?.includes('image/jpeg') ||
    file?.content_type?.includes('image/jpg') ||
    file?.content_type?.includes('image/gif') ||
    file?.content_type?.includes('image/webp') ||
    file?.content_type?.includes('image/bmp'),
  isFileImageOtherType: (file) => file?.content_type?.includes('image/') && !fileTypeChecks.isFileImage(file),
  isFileVideo: (file) => file?.content_type?.includes('video'),
  isFileAudio: (file) => file?.content_type?.includes('audio'),
  isFilePdf: (file) => file?.content_type?.includes('pdf'),
  isFileOfficeDocument: (file) => file?.content_type?.includes('application'),
  isFileText: (file) => file?.content_type?.includes('text'),
  isFileQuote: (file) => file?.entity === 'Quote' || file?.entity === 'QuoteSet',
  isFileProperty: (file) =>
    (file?.user_id && (file?.status || file?.preview_file_id || (file?.status && file?.preview_file_id))) ||
    file?.entity === 'Property',
  isFileXml: (file) => file?.content_type?.includes('application/xml'),
  isFileArtwork: (file) => file?.content_type?.includes('data/artwork'),
};

const fileIcons = {
  isFileImage: () => <ImageOutlined sx={{ color: 'secondary.dark' }} />,
  isFileImageOtherType: () => <ImageOutlined sx={{ color: 'secondary.dark' }} />,
  isFileVideo: () => <VideocamOutlined sx={{ color: 'secondary.dark' }} />,
  isFileAudio: () => <HeadphonesOutlined sx={{ color: 'secondary.dark' }} />,
  isFilePdf: () => <PictureAsPdfOutlined sx={{ color: 'secondary.dark' }} />,
  isFileOfficeDocument: () => <InsertDriveFileOutlined sx={{ color: 'secondary.dark' }} />,
  isFileText: () => <TextSnippetOutlined sx={{ color: 'secondary.dark' }} />,
  isFileQuote: () => <RequestQuoteOutlined sx={{ color: 'secondary.dark' }} />,
  isFileProperty: () => (
    <span
      style={{
        backgroundColor: '#454746',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        width: 24,
        height: 23,
      }}
    >
      <HomeOutlined sx={{ fontSize: 18, color: 'common.white' }} />
    </span>
  ),
  isFileXml: () => <ArticleOutlined sx={{ color: 'secondary.dark' }} />,
  isFileArtwork: () => <ColorLensOutlined sx={{ color: 'secondary.dark' }} />,
  default: () => <DescriptionOutlined sx={{ color: 'secondary.dark' }} />,
};

export const getFileIcon = (file, selected = false) => {
  for (const [check, icon] of Object.entries(fileIcons)) {
    if (fileTypeChecks[check]?.(file)) {
      return icon(selected);
    }
  }
  return file.format === 'Folder' ||
    file?.type === 'Organisation' ||
    file?.type === 'Group' ||
    file?.type === 'SubGroup' ? (
    <FolderOpenOutlined sx={{ color: 'secondary.dark' }} />
  ) : (
    fileIcons.default(selected)
  );
};

export const hasFileIconPreview = (file) => {
  return Object.keys(fileTypeChecks).some((check) => fileTypeChecks[check]?.(file));
};

const previewIconStyles = { fontSize: 70, color: 'secondary.dark' };

const filePreviewIcons = {
  isFileImageOtherType: <ImageOutlined sx={previewIconStyles} />,
  isFileQuote: <RequestQuoteOutlined sx={previewIconStyles} />,
  isFileVideo: <VideocamOutlined sx={previewIconStyles} />,
  isFileAudio: <HeadphonesOutlined sx={previewIconStyles} />,
  isFilePdf: <PictureAsPdfOutlined sx={previewIconStyles} />,
  isFileOfficeDocument: <InsertDriveFileOutlined sx={previewIconStyles} />,
  isFileText: <TextSnippetOutlined sx={previewIconStyles} />,
  isFileProperty: <HomeOutlined sx={previewIconStyles} />,
  isFileXml: <ArticleOutlined sx={previewIconStyles} />,
  isFileArtwork: <ColorLensOutlined sx={previewIconStyles} />,
  default: <DescriptionOutlined sx={previewIconStyles} />,
};

export const getFileIconPreview = (file) => {
  for (const [check, icon] of Object.entries(filePreviewIcons)) {
    if (fileTypeChecks[check]?.(file)) {
      return icon;
    }
  }
  return filePreviewIcons.default;
};

const bigPreviewIconStyles = { fontSize: '100px' };

export const getFileIconLarge = (file) => {
  for (const [check, icon] of Object.entries(filePreviewIcons)) {
    if (fileTypeChecks[check]?.(file)) {
      return cloneElement(icon, { sx: bigPreviewIconStyles });
    }
  }

  // Fallbacks for fileType/type/entity
  if (!file) {
    return <DescriptionOutlined sx={bigPreviewIconStyles} />;
  }

  // Explicitly check for image types
  if (fileTypeChecks.isFileImage(file)) {
    return <ImageOutlined sx={bigPreviewIconStyles} />;
  }

  if (file?.format === 'Folder') {
    return <FolderOpenOutlined sx={bigPreviewIconStyles} />;
  }

  switch (file?.type) {
    case 'Organisation':
      return <FolderOpenOutlined sx={bigPreviewIconStyles} />;
    case 'Group':
      return <PeopleOutline sx={bigPreviewIconStyles} />;
    case 'SubGroup':
      return <PeopleOutline sx={bigPreviewIconStyles} />;
    default:
      break;
  }

  if (file?.entity === 9 || file?.entity === 'QuoteSet') {
    return <RequestQuoteOutlined sx={bigPreviewIconStyles} />;
  }

  return <DescriptionOutlined sx={bigPreviewIconStyles} />;
};

export const hasFileDialogPreview = (file) => {
  return fileTypeChecks.isFileImage(file) || fileTypeChecks.isFilePdf(file) || fileTypeChecks.isFileVideo(file);
};
